// The 15 edges a provider row can travel, as ONE table. Three of them closed
// states that were dead before: installing had no timeout (spinner forever), a
// non-zero sign-in exit was silent, and a refused ACP add was unreachable copy.
import { isInFlight, type ProviderRowStatus } from "./provider-status.js";

/** The RPC the Remove action needs. Gated on the daemon's own `meta.capabilities`
 *  — NOT on its version (frozen at 0.1.105) and NOT on catching an error: an older
 *  daemon behind a NEW relay answers nothing at all, so trying it would hang on
 *  the client's 15 s timer with the button still spinning. */
export function daemonSupportsProviderSignIn(capabilities: readonly string[] | undefined): boolean {
  return capabilities?.includes("provider_sign_in") === true;
}

export type GuidedSignInProvider = "claude" | "codex";

// The provider the daemon itself can sign in; the row button and the pane both ask this.
export function guidedSignInProvider(
  providerId: string,
  machine: { ownsMachine: boolean; capabilities: readonly string[] | undefined },
): GuidedSignInProvider | undefined {
  if (!machine.ownsMachine || !daemonSupportsProviderSignIn(machine.capabilities)) return undefined;
  return providerId === "claude" || providerId === "codex" ? providerId : undefined;
}

export const ACP_REMOVE_CAPABILITY = "remove_acp_provider";

/** Absent capabilities = an older daemon that cannot tell us, so fail CLOSED. */
export function daemonSupportsAcpRemove(capabilities: readonly string[] | undefined): boolean {
  return capabilities?.includes(ACP_REMOVE_CAPABILITY) === true;
}

/** No answer in this long and `installing` becomes "Couldn't check", not a spinner. */
export const INSTALL_WATCHDOG_MS = 600_000;

/** Per machine. The Re-check button shows the countdown instead of refusing silently. */
export const RECHECK_THROTTLE_MS = 30_000;

export type ProviderFlowEvent =
  | { type: "probe_answer"; status: ProviderRowStatus }
  | { type: "install" }
  | { type: "install_ok" }
  | { type: "install_failed" }
  // `note`: the daemon ANSWERED `timed_out`, and this is its sentence (from its steps).
  | { type: "install_timeout"; note?: string }
  | { type: "install_stop_waiting" }
  | { type: "sign_in" }
  | { type: "sign_in_closed"; exitCode: number | null }
  | { type: "acp_add" }
  | { type: "acp_added"; available: boolean | null; binary?: string | null }
  | { type: "acp_refused" }
  | { type: "acp_remove" }
  | { type: "key_saved"; valid: boolean }
  | { type: "machine_offline" };

export interface ProviderFlowGuards {
  /** canInstallOn() said yes AND this surface allows it. */
  canInstall: boolean;
  /** Desktop bridge + own daemon + hostname match. */
  canSignIn: boolean;
  /** The daemon advertises ACP_REMOVE_CAPABILITY. */
  canRemoveAcp: boolean;
  /** Foreign machine: the install runs there, so the notice comes first. */
  remoteInstall?: boolean;
}

export type ProviderFlowResult =
  | {
      kind: "move";
      to: ProviderRowStatus;
      note?: string;
      confirm?: "remote-install";
    }
  // refused = reachable and must be told about; noop = the event has no edge here.
  | { kind: "refused"; reason: string }
  | { kind: "noop" };

const NOOP: ProviderFlowResult = { kind: "noop" };

const SIGN_IN_FROM = new Set<ProviderRowStatus>(["signed-out", "expired", "unverified"]);
const INSTALL_FROM = new Set<ProviderRowStatus>(["not-installed", "unverified", "install-failed"]);

function installFlow(
  from: ProviderRowStatus,
  event: ProviderFlowEvent,
  guards: ProviderFlowGuards,
): ProviderFlowResult {
  if (event.type === "install") {
    if (!INSTALL_FROM.has(from)) return NOOP;
    if (!guards.canInstall) {
      return {
        kind: "refused",
        reason: "This machine won't install it from here — run the command on it.",
      };
    }
    return guards.remoteInstall
      ? { kind: "move", to: "installing", confirm: "remote-install" }
      : { kind: "move", to: "installing" };
  }
  if (from !== "installing") return NOOP;
  if (event.type === "install_ok") {
    return { kind: "move", to: "signed-out", note: "Installed. Signing in is the last step." };
  }
  if (event.type === "install_failed") return { kind: "move", to: "install-failed" };
  // Nothing on the wire cancels an install (#3860 U4): this only stops the spinner.
  if (event.type === "install_stop_waiting") {
    return {
      kind: "move",
      to: "unverified",
      note: "You stopped waiting. The install is still running on that machine and may finish — check again.",
    };
  }
  return {
    kind: "move",
    to: "unverified",
    note:
      (event.type === "install_timeout" ? event.note : undefined) ??
      "Setup is taking longer than expected. It may have finished — check again.",
  };
}

function signInFlow(
  from: ProviderRowStatus,
  event: ProviderFlowEvent,
  guards: ProviderFlowGuards,
): ProviderFlowResult {
  if (event.type === "sign_in") {
    if (!SIGN_IN_FROM.has(from)) return NOOP;
    if (!guards.canSignIn) {
      return { kind: "refused", reason: "Sign-in has to happen on that machine itself." };
    }
    return { kind: "move", to: "signing" };
  }
  if (from !== "signing" || event.type !== "sign_in_closed") return NOOP;
  if (event.exitCode === 0) return { kind: "move", to: "ready" };
  return { kind: "move", to: "signed-out", note: "The sign-in didn't finish. Nothing changed." };
}

function acpFlow(
  from: ProviderRowStatus,
  event: ProviderFlowEvent,
  guards: ProviderFlowGuards,
): ProviderFlowResult {
  if (event.type === "acp_add") {
    return from === "available" ? { kind: "move", to: "adding" } : NOOP;
  }
  if (event.type === "acp_remove") {
    if (from !== "acp-ready" && from !== "acp-missing") return NOOP;
    if (!guards.canRemoveAcp) {
      return {
        kind: "refused",
        reason: "That machine's Cyborg can't remove an ACP agent yet — update it.",
      };
    }
    return { kind: "move", to: "available" };
  }
  if (from !== "adding") return NOOP;
  if (event.type === "acp_refused") {
    return { kind: "move", to: "acp-failed", note: "That machine refused to register it." };
  }
  if (event.type !== "acp_added") return NOOP;
  // `binary: null` = the daemon had nothing to look for (an npx-style entry that
  // fetches itself), so "CLI not found" would report a check that never ran.
  // `undefined` = an older daemon that doesn't say, so fall back to `available`.
  if (event.binary === null) return { kind: "move", to: "acp-ready" };
  return event.available === false
    ? { kind: "move", to: "acp-missing" }
    : { kind: "move", to: "acp-ready" };
}

const INSTALL_EVENTS = new Set([
  "install",
  "install_ok",
  "install_failed",
  "install_timeout",
  "install_stop_waiting",
]);
const SIGN_IN_EVENTS = new Set(["sign_in", "sign_in_closed"]);
const ACP_EVENTS = new Set(["acp_add", "acp_added", "acp_refused", "acp_remove"]);

export function providerTransition(
  from: ProviderRowStatus,
  event: ProviderFlowEvent,
  guards: ProviderFlowGuards,
): ProviderFlowResult {
  if (event.type === "machine_offline") {
    return {
      kind: "move",
      to: "unverified",
      note: "Nothing here is known while the machine is offline — this is the last snapshot.",
    };
  }
  if (event.type === "probe_answer") {
    // In-flight owns the row until its RPC answers, exactly like installedNow.
    return isInFlight(from) ? NOOP : { kind: "move", to: event.status };
  }
  if (INSTALL_EVENTS.has(event.type)) return installFlow(from, event, guards);
  if (SIGN_IN_EVENTS.has(event.type)) return signInFlow(from, event, guards);
  if (ACP_EVENTS.has(event.type)) return acpFlow(from, event, guards);
  if (event.type !== "key_saved") return NOOP;
  if (from !== "needs-key" && from !== "key-invalid") return NOOP;
  return event.valid
    ? { kind: "move", to: "ready" }
    : {
        kind: "move",
        to: "key-invalid",
        note: "That key was rejected. It is stored for this machine only.",
      };
}
